import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, MapPin, Clock, ChevronRight, Send, Loader2, CheckCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { submitForm } from '../lib/api';
import Navbar from '../components/Navbar';
import { Footer } from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const jobTypeLabels = {
  fr: { full_time: 'Temps plein', part_time: 'Temps partiel', contract: 'Contrat', internship: 'Stage', remote: 'Télétravail' },
  en: { full_time: 'Full-time', part_time: 'Part-time', contract: 'Contract', internship: 'Internship', remote: 'Remote' }
};

export default function JobsPage() {
  const { language } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApply, setShowApply] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/jobs`);
      const data = await res.json();
      setJobs(data);
    } catch (err) { console.error('Failed to load jobs:', err); }
    finally { setLoading(false); }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const jobTitle = language === 'fr' ? selectedJob.title_fr : selectedJob.title_en;
      await submitForm({
        form_type: 'contact',
        name: formData.name,
        email: formData.email,
        subject: `Candidature: ${jobTitle}`,
        message: formData.message
      });
      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => { setSuccess(false); setShowApply(false); }, 3000);
    } catch (err) { console.error('Failed to submit:', err); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B1521]" data-testid="jobs-page">
      <Navbar />

      <section className="pt-32 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[#4A90D9] text-sm font-semibold tracking-wider uppercase">
              {language === 'fr' ? 'Carrières' : 'Careers'}
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-3">
              {language === 'fr' ? 'Rejoignez notre équipe' : 'Join our team'}
            </h1>
            <p className="text-white/60 mt-4 max-w-2xl mx-auto text-lg">
              {language === 'fr'
                ? "Nous recherchons des talents passionnés pour construire l'avenir du digital."
                : "We're looking for passionate talent to build the future of digital."}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-20"><Briefcase size={48} className="mx-auto text-white/20 mb-4 animate-pulse" /></div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-20 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl">
              <Briefcase size={48} className="mx-auto text-white/20 mb-4" />
              <p className="text-white/60 text-lg">{language === 'fr' ? "Aucun poste ouvert pour le moment." : 'No open positions at the moment.'}</p>
              <p className="text-white/40 text-sm mt-2">{language === 'fr' ? "Revenez bientôt ou envoyez-nous une candidature spontanée." : 'Come back soon or send us a spontaneous application.'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job, index) => (
                <motion.div
                  key={job.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-[#4A90D9]/40 transition-colors cursor-pointer"
                  onClick={() => { setSelectedJob(selectedJob?.id === job.id ? null : job); setShowApply(false); }}
                  data-testid={`job-${job.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-white">{language === 'fr' ? job.title_fr : job.title_en}</h3>
                      <div className="flex flex-wrap gap-3 mt-2">
                        {job.department && <span className="text-[#4A90D9] text-sm font-medium">{job.department}</span>}
                        {job.location && <span className="flex items-center gap-1 text-white/50 text-sm"><MapPin size={14} />{job.location}</span>}
                        <span className="flex items-center gap-1 text-white/50 text-sm"><Clock size={14} />{jobTypeLabels[language][job.job_type] || job.job_type}</span>
                        {job.salary_range && <span className="text-white/50 text-sm">{job.salary_range}</span>}
                      </div>
                    </div>
                    <ChevronRight size={20} className={`text-white/40 transition-transform ${selectedJob?.id === job.id ? 'rotate-90' : ''}`} />
                  </div>

                  {selectedJob?.id === job.id && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-6 pt-6 border-t border-white/10">
                      <div className="text-white/70 leading-relaxed whitespace-pre-wrap mb-4">
                        {language === 'fr' ? job.description_fr : job.description_en}
                      </div>
                      {(job.requirements_fr || job.requirements_en) && (
                        <div className="mt-4">
                          <h4 className="text-white font-semibold mb-2">{language === 'fr' ? 'Exigences' : 'Requirements'}</h4>
                          <div className="text-white/60 whitespace-pre-wrap text-sm">
                            {language === 'fr' ? job.requirements_fr : job.requirements_en}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowApply(!showApply); }}
                        className="mt-6 px-6 py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors flex items-center gap-2"
                        data-testid="apply-btn"
                      >
                        <Send size={16} />
                        {language === 'fr' ? 'Postuler' : 'Apply'}
                      </button>

                      {showApply && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-6 bg-[#152840] rounded-xl p-6 border border-white/10" onClick={(e) => e.stopPropagation()}>
                          {success ? (
                            <div className="text-center py-4">
                              <CheckCircle size={40} className="mx-auto text-green-500 mb-3" />
                              <p className="text-white font-medium">{language === 'fr' ? 'Candidature envoyée !' : 'Application sent!'}</p>
                            </div>
                          ) : (
                            <form onSubmit={handleApply} className="space-y-4">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder={language === 'fr' ? 'Votre nom' : 'Your name'} className="px-4 py-3 rounded-lg input-dark" data-testid="apply-name" />
                                <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder={language === 'fr' ? 'Votre email' : 'Your email'} className="px-4 py-3 rounded-lg input-dark" data-testid="apply-email" />
                              </div>
                              <textarea required rows={4} value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder={language === 'fr' ? 'Parlez-nous de vous et pourquoi vous êtes intéressé(e)...' : 'Tell us about yourself and why you are interested...'} className="w-full px-4 py-3 rounded-lg input-dark resize-none" data-testid="apply-message" />
                              <button type="submit" disabled={submitting} className="px-6 py-3 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors flex items-center gap-2 disabled:opacity-50" data-testid="apply-submit">
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                {language === 'fr' ? 'Envoyer ma candidature' : 'Send my application'}
                              </button>
                            </form>
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
